import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import dayjs from "dayjs";
import { Button, Typography } from "@mui/material";
import { SummaryItem } from "components/common/Summary";
import { titleCase } from "title-case";
import { GET_PLATFORM_EVENT_LOGS } from "graphql/eventLogs";
import { Drawer, Select, TablePage } from "@outpave/ds";
import Summary from "components/common/Summary";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import {
  buildGenericItems,
  buildImpersonationItems,
  extractEventLogRows,
  formatActor,
  type EventLogRow,
} from "./eventLogsUtils";

const SERVICES = [
  "ALL",
  "IMPERSONATION",
  "ACCOUNTS",
  "USERS",
  "PLATFORM",
] as const;
type ServiceFilter = (typeof SERVICES)[number];

const EventLogs: React.FC = () => {
  const { t } = useTranslation();
  const [serviceFilter, setServiceFilter] = useState<ServiceFilter>("ALL");
  const [selectedLog, setSelectedLog] = useState<EventLogRow | null>(null);

  const initialVariables = useMemo(
    () => ({
      service: serviceFilter === "ALL" ? undefined : serviceFilter,
      first: 25,
      after: undefined,
    }),
    [serviceFilter],
  );

  const { dataArray, displayLoading, handleScroll } = useInfiniteScroll({
    query: GET_PLATFORM_EVENT_LOGS,
    initialVariables,
    extractData: extractEventLogRows,
  });

  const rows: EventLogRow[] = dataArray as EventLogRow[];

  const columns: DataColumn<EventLogRow>[] = useMemo(
    () => [
      {
        id: "when",
        header: t("When"),
        cell: (row) =>
          row.occurredAt
            ? dayjs(row.occurredAt).format("YYYY/MM/DD HH:mm:ss")
            : "-",
      },
      {
        id: "service",
        header: t("Service"),
        cell: (row) => row.service,
      },
      {
        id: "action",
        header: t("Action"),
        cell: (row) => (
          <Typography variant="body2" noWrap>
            {row.label}
          </Typography>
        ),
      },
      {
        id: "actor",
        header: t("Actor"),
        cell: (row) => (
          <Typography variant="body2" noWrap>
            {formatActor(row.actor)}
          </Typography>
        ),
      },
    ],
    [t],
  );

  // Matches the "Account: All" / "Acme Corp" pattern used by the Platform
  // Users page (AccountSearchAutoComplete) so the default shows the filter
  // dimension; chosen values show plain.
  const serviceLabel = (s: ServiceFilter) =>
    s === "ALL" ? t("ServiceAll") : titleCase(s.toLowerCase());

  const drawerTitle = selectedLog
    ? titleCase(selectedLog.service.toLowerCase())
    : "";

  const detailItems: SummaryItem[] = useMemo(() => {
    if (!selectedLog) return [];
    return selectedLog.service === "IMPERSONATION"
      ? buildImpersonationItems(
          selectedLog.parsed,
          selectedLog.occurredAt,
          selectedLog.actor,
          selectedLog.label,
          t,
        )
      : buildGenericItems(
          selectedLog.parsed,
          selectedLog.occurredAt,
          selectedLog.actor,
          selectedLog.label,
          t,
        );
  }, [selectedLog, t]);

  return (
    <TablePage
      title={t("Event Logs")}
      tabs={{
        items: [{ id: "all", label: t("Show All") }],
        activeId: "all",
        onChange: () => undefined,
      }}
      filters={
        <Select
          options={SERVICES.map((s) => ({ id: s, label: serviceLabel(s) }))}
          value={serviceFilter}
          onChange={(id) => id && setServiceFilter(id as ServiceFilter)}
          placeholder={t("Service")}
          aria-label={t("Service")}
          // "ALL" is already an option, so the field is never empty; required
          // is how the DS suppresses a clear that would only snap back.
          required
          size="md"
          width={360}
        />
      }
    >
      <DataTable<EventLogRow>
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.id}
        loading={displayLoading}
        onRowClick={(row) => setSelectedLog(row)}
        onScroll={handleScroll}
        emptyState={t("No event logs found")}
        stickyHeader
      />

      <Drawer
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        title={[t(drawerTitle)]}
        footer={
          <Button
            variant="default-outlined"
            onClick={() => setSelectedLog(null)}
          >
            {t("Exit")}
          </Button>
        }
      >
        <Summary items={detailItems} disableContainer />
      </Drawer>
    </TablePage>
  );
};

export default EventLogs;
